import { EngineTriggerTypeEnum, getEngineTriggerString } from "../enums/engine-trigger-type.enum";
import { getLogExecutionResultString, LogExecutionResult } from "../enums/log-execution-result.enum";
import { EngineTrigger } from "../interfaces/engine-trigger.interface";

/** Icon zum Auslöser der Schaltung (Zeitschaltuhr, manuelle Bedienung, Sonderprogramm, ...). */
export function getEngineResultIcon(engineResult: EngineTrigger | undefined): string {
    if (!engineResult) { return ''; }
    switch (engineResult.trigger) {
        case EngineTriggerTypeEnum.Timer:
        case EngineTriggerTypeEnum.MQ_Timer:
        case EngineTriggerTypeEnum.DI_Timer:
            return 'assets/icons/stopwatch.svg';
        case EngineTriggerTypeEnum.Manual:
        case EngineTriggerTypeEnum.MQ_Manual:
        case EngineTriggerTypeEnum.DI_Manual:
            return 'assets/icons/back_hand_material.svg';
        case EngineTriggerTypeEnum.Scenario:
        case EngineTriggerTypeEnum.Scenario_LowPriority:
        case EngineTriggerTypeEnum.MQ_Scenario:
        case EngineTriggerTypeEnum.MQ_Scenario_LowPriority:
        case EngineTriggerTypeEnum.DI_Scenario:
        case EngineTriggerTypeEnum.DI_Scenario_LowPriority:
            return 'assets/icons/play-btn.svg';
        case EngineTriggerTypeEnum.R210:
        case EngineTriggerTypeEnum.R2110:
        case EngineTriggerTypeEnum.R2210:
        case EngineTriggerTypeEnum.MARZ:
        case EngineTriggerTypeEnum.DI_AUTO:
        case EngineTriggerTypeEnum.MQ_Action:
        case EngineTriggerTypeEnum.NO_DATA:
        case EngineTriggerTypeEnum.Timeout_no_MQ_data:
        case EngineTriggerTypeEnum.Timeout_no_DI_data:
        case EngineTriggerTypeEnum.LongitudinalAdjustment:
        case EngineTriggerTypeEnum.TransverseAdjustment:
        case EngineTriggerTypeEnum.Unknown:
            // Regelwerk/EngineParameter — gleiches Icon wie im Bilder-Tab (getRefTypeIcon 'EP');
            // auch die NO_DATA-/Timeout-Fälle sind Ergebnisse der Engine-Regelverarbeitung.
            // Unknown: die Schaltung kam von der Engine, nur der Auslöser wurde nicht
            // mitgeschrieben — deshalb ebenfalls das Zahnrad statt eines Fragezeichens
            return 'assets/icons/gear-fill.svg';
        default: // Init
            return 'assets/icons/question-circle_grey.svg';
    }
}

/**
 * Zusatz-Symbol des auslösenden Elements, wenn der Auslöser nicht von der Anzeige
 * selbst kommt: Radar bei MQ-Auslösern, Eingangs-Symbol bei DI-Auslösern.
 * Leer bei allen Auslösern der Anzeige selbst (dann nur das Auslöser-Icon).
 */
export function getEngineResultSourceIcon(engineResult: EngineTrigger | undefined): string {
    if (!engineResult) { return ''; }
    switch (engineResult.trigger) {
        case EngineTriggerTypeEnum.MQ_Scenario:
        case EngineTriggerTypeEnum.MQ_Scenario_LowPriority:
        case EngineTriggerTypeEnum.MQ_Manual:
        case EngineTriggerTypeEnum.MQ_Timer:
        case EngineTriggerTypeEnum.MQ_Action:
        case EngineTriggerTypeEnum.Timeout_no_MQ_data:
            return 'assets/icons/data-end-channel/radar.svg';
        case EngineTriggerTypeEnum.DI_AUTO:
        case EngineTriggerTypeEnum.DI_Scenario:
        case EngineTriggerTypeEnum.DI_Scenario_LowPriority:
        case EngineTriggerTypeEnum.DI_Manual:
        case EngineTriggerTypeEnum.DI_Timer:
        case EngineTriggerTypeEnum.Timeout_no_DI_data:
            return 'assets/icons/inputIcon_black.svg';
        default:
            return '';
    }
}

/** Statustext einer Schaltung: LogExecutionResult (falls vorhanden), sonst Konsistenz der Rückmeldung. */
export function getEngineResultStatusText(engineResult: EngineTrigger | undefined): string {
    if (!engineResult) { return ''; }
    let statusText: string;
    if (engineResult.result != null) {
        statusText = getLogExecutionResultString(engineResult.result);
        // Ist-Zustand weicht inzwischen vom Soll ab (z. B. Defekt) — Confirmed ist nur
        // das damalige Schaltergebnis, die Konsistenz ist die aktuelle Momentaufnahme
        if (engineResult.isDataConsistent === false && engineResult.result !== LogExecutionResult.Pending) {
            statusText += ' — Ist-Zustand weicht ab!';
        }
    } else if (engineResult.isDataConsistent === true) {
        statusText = 'Rückmeldung bestätigt';
    } else if (engineResult.isDataConsistent === false) {
        statusText = 'Rückmeldung weicht ab';
    } else {
        statusText = 'Warte auf Rückmeldung';
    }
    // Unbekannter Auslöser wird nicht benannt — der Status allein ist die Aussage
    let triggerText = engineResult.trigger === EngineTriggerTypeEnum.Unknown
        ? '' : getEngineTriggerString(engineResult.trigger);
    if (triggerText && engineResult.triggerName) {
        triggerText += ' „' + engineResult.triggerName + '“';
    }
    return triggerText ? 'Auslöser: ' + triggerText + ' | ' + statusText : statusText;
}

/** CSS-Klasse für Statusfarbe einer Schaltung (grün/rot/gelb). */
export function getEngineResultStatusClass(engineResult: EngineTrigger | undefined): string {
    if (!engineResult) { return ''; }
    // Laufende Schaltung bleibt gelb — eine abweichende Rückmeldung ist hier normal,
    // solange die Station den neuen Zustand noch nicht gemeldet hat
    if (engineResult.result === LogExecutionResult.Pending) { return 'avs-engine-status-amber'; }
    // Aktuelle Abweichung Ist vs. Soll überstimmt ein früheres Confirmed
    // (Station zeigt nicht mehr den bestätigten Zustand, z. B. Defekt)
    if (engineResult.isDataConsistent === false) { return 'avs-engine-status-red'; }
    if (engineResult.result != null) {
        switch (engineResult.result) {
            case LogExecutionResult.Confirmed:
                return 'avs-engine-status-green';
            case LogExecutionResult.EvaFailed:
            case LogExecutionResult.Timeout:
            case LogExecutionResult.Offline:
            case LogExecutionResult.Nack:
                return 'avs-engine-status-red';
            default:
                return 'avs-engine-status-amber';
        }
    }
    if (engineResult.isDataConsistent === true) { return 'avs-engine-status-green'; }
    return 'avs-engine-status-amber';
}
